// Step 3 (remote target only): how to reach the machine.
//
// Three questions, in the order they get answered: which machine, what opens it,
// and does that work. This used to be one column of nine controls with the
// interesting one — Test connection — buried in the middle, which made the probe
// read like another option rather than the thing that turns a several-minute
// failed deploy into a two-second answer.
//
// The credential group is the one form in the console that collects a secret the
// manager will hold rather than merely check, so it says so rather than burying
// it. Picking a saved key is the path where the secret never enters the browser
// at all: no endpoint returns key material, so a library key is only ever used by
// id.

import type { ProvisionRemoteCheckResponse } from "@schemas/provision.ts";
import { useMutation } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import { Checkbox } from "@veil/ui/components/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@veil/ui/components/collapsible";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
	FieldLegend,
	FieldSet,
} from "@veil/ui/components/field";
import { RadioGroup } from "@veil/ui/components/radio-group";
import { Skeleton } from "@veil/ui/components/skeleton";
import { Spinner } from "@veil/ui/components/spinner";
import { Switch } from "@veil/ui/components/switch";
import { formatRelative } from "@veil/ui/lib/format";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import CircleAlert from "lucide-react/dist/esm/icons/circle-alert";
import CircleCheck from "lucide-react/dist/esm/icons/circle-check";
import CircleX from "lucide-react/dist/esm/icons/circle-x";
import KeyRound from "lucide-react/dist/esm/icons/key-round";
import PlugZap from "lucide-react/dist/esm/icons/plug-zap";
import TriangleAlert from "lucide-react/dist/esm/icons/triangle-alert";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { ChoiceCard } from "@/components/form/choice-card";
import { PrivateKeyField } from "@/components/form/private-key-field";
import { TextField } from "@/components/form/text-field";
import { api, unwrap } from "@/lib/api";
import {
	type SshKeyMetadata,
	shortFingerprint,
	useSshKeys,
} from "@/lib/ssh-keys";
import {
	CONNECTION_PROBE_FIELDS,
	type DeployWorkerValue,
	NEW_SSH_KEY,
	toSshRequest,
} from "../schema";

export function ConnectionStep({
	form,
}: {
	form: UseFormReturn<DeployWorkerValue>;
}) {
	const errors = form.formState.errors;
	const keys = useSshKeys();

	const check = useMutation({
		mutationFn: async (): Promise<ProvisionRemoteCheckResponse> =>
			(await unwrap(
				api.api.workers.provision.remote.check.post({
					...toSshRequest(form.getValues()),
					hostname: form.getValues("name") || undefined,
				}),
			)) as ProvisionRemoteCheckResponse,
	});

	// Validate the fields the probe needs before spending ten seconds finding out
	// the host was blank.
	const test = async () => {
		const ok = await form.trigger([...CONNECTION_PROBE_FIELDS], {
			shouldFocus: true,
		});
		if (ok) check.mutate();
	};

	return (
		<div className="grid gap-7">
			<FieldSet>
				<GroupLegend>Machine</GroupLegend>
				<div className="grid grid-cols-[1fr_6rem] gap-3">
					<TextField
						id="deploy-ssh-host"
						label="Machine address"
						placeholder="203.0.113.10"
						autoComplete="off"
						spellCheck={false}
						error={errors.sshHost}
						description="An IP address or hostname this manager can reach over SSH."
						{...form.register("sshHost", { onChange: () => check.reset() })}
					/>
					<TextField
						id="deploy-ssh-port"
						label="SSH port"
						inputMode="numeric"
						autoComplete="off"
						error={errors.sshPort}
						{...form.register("sshPort", { onChange: () => check.reset() })}
					/>
				</div>
				<TextField
					id="deploy-ssh-user"
					label="SSH user"
					placeholder="root"
					autoComplete="off"
					spellCheck={false}
					className="max-w-xs"
					error={errors.sshUser}
					description="Needs to be root, or have passwordless sudo — it has to manage Docker."
					{...form.register("sshUser", { onChange: () => check.reset() })}
				/>
			</FieldSet>

			<FieldSet>
				<GroupLegend>Authentication</GroupLegend>
				<AuthenticationGroup
					form={form}
					keys={keys.data ?? []}
					loading={keys.isLoading}
					onKeyChanged={() => check.reset()}
				/>
			</FieldSet>

			<FieldSet>
				<GroupLegend>Verification</GroupLegend>
				<div className="flex flex-wrap items-center gap-3">
					<Button
						type="button"
						onClick={() => void test()}
						disabled={check.isPending}
					>
						{check.isPending ? (
							<Spinner className="size-3" />
						) : (
							<PlugZap className="size-3" />
						)}
						{check.isPending ? "Testing…" : "Test connection"}
					</Button>
					<span className="text-xs text-muted-foreground">
						Reads the machine only. Nothing is installed or changed.
					</span>
				</div>

				{check.isError ? (
					<CheckNote
						tone="error"
						icon={CircleX}
						title="Could not run the check"
					>
						{check.error instanceof Error
							? check.error.message
							: "The request failed before it reached that machine."}
					</CheckNote>
				) : null}
				{check.data ? <CheckReport report={check.data} /> : null}
				{!check.data && !check.isError && !check.isPending ? (
					<p className="text-xs text-muted-foreground">
						Optional, and worth the ten seconds: it reports whether the machine
						answers, whether this user can manage Docker, and whether the worker
						port is free, before anything is written.
					</p>
				) : null}
			</FieldSet>

			<Options form={form} facts={check.data?.facts ?? null} />
		</div>
	);
}

/** Group legend as the step's small-caps eyebrow, matching the setup panels. */
function GroupLegend({ children }: { children: ReactNode }) {
	return (
		<FieldLegend
			variant="label"
			className="text-[0.68rem] tracking-[0.14em] text-muted-foreground uppercase"
		>
			{children}
		</FieldLegend>
	);
}

/**
 * Pick a saved key, or bring one.
 *
 * With no saved keys there is no choice to make, so there is no picker: a radio
 * group of one option is a control that asks a question with a single answer.
 * The library is offered as a checkbox on the paste form instead, which is where
 * the operator already is.
 */
function AuthenticationGroup({
	form,
	keys,
	loading,
	onKeyChanged,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	keys: SshKeyMetadata[];
	loading: boolean;
	onKeyChanged: () => void;
}) {
	if (loading) {
		return <Skeleton className="h-20" />;
	}
	if (keys.length === 0) {
		return <NewKeyFields form={form} onKeyChanged={onKeyChanged} />;
	}
	return <KeyPicker form={form} keys={keys} onKeyChanged={onKeyChanged} />;
}

function KeyPicker({
	form,
	keys,
	onKeyChanged,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	keys: SshKeyMetadata[];
	onKeyChanged: () => void;
}) {
	const selected = form.watch("sshKeyId");

	return (
		<Controller
			control={form.control}
			name="sshKeyId"
			render={({ field }) => (
				<RadioGroup
					value={field.value}
					onValueChange={(value) => {
						field.onChange(value);
						onKeyChanged();
						// A saved key carries its own default login, and the whole point
						// of naming keys after the machines they open is that the user
						// usually differs between them.
						const picked = keys.find((k) => k.id === value);
						if (picked) {
							form.setValue("sshUser", picked.sshUser, { shouldDirty: true });
						}
					}}
					aria-label="Which key opens this machine"
					className="gap-2"
				>
					{keys.map((key) => (
						<ChoiceCard
							key={key.id}
							id={`deploy-ssh-key-${key.id}`}
							value={key.id}
							icon={KeyRound}
							title={key.name}
							description={
								<span className="flex flex-col gap-0.5">
									<span
										className="font-mono text-[11px] break-all"
										title={key.fingerprint ?? undefined}
									>
										{shortFingerprint(key.fingerprint)}
									</span>
									<span>
										Logs in as <code>{key.sshUser}</code>
										{key.lastUsedAt
											? ` · last used ${formatRelative(key.lastUsedAt)}`
											: " · never used yet"}
										{key.usedBy.length > 0
											? ` · opens ${key.usedBy.join(", ")}`
											: ""}
									</span>
								</span>
							}
						/>
					))}
					<ChoiceCard
						id="deploy-ssh-key-new"
						value={NEW_SSH_KEY}
						title="Use a new key"
						description="Paste one for this deploy, and optionally add it to the library."
					/>
					{selected === NEW_SSH_KEY ? (
						<div className="ml-1 border-l pl-4">
							<NewKeyFields form={form} onKeyChanged={onKeyChanged} />
						</div>
					) : null}
				</RadioGroup>
			)}
		/>
	);
}

/** The paste form, plus the offer to keep it. */
function NewKeyFields({
	form,
	onKeyChanged,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	onKeyChanged: () => void;
}) {
	const keyId = useId();
	const errors = form.formState.errors;
	const saveKey = form.watch("saveKey");

	return (
		<div className="grid gap-4">
			<PrivateKeyField
				id={keyId}
				error={errors.privateKey}
				registration={form.register("privateKey", {
					// A pasted key that turns out to be wrong should not be judged
					// against a probe result from the previous one.
					onChange: onKeyChanged,
				})}
				onFileText={(text) => {
					form.setValue("privateKey", text, {
						shouldValidate: true,
						shouldDirty: true,
					});
					onKeyChanged();
				}}
			/>

			<TextField
				id="deploy-ssh-passphrase"
				label="Key passphrase"
				type="password"
				autoComplete="off"
				className="max-w-xs"
				error={errors.passphrase}
				description="Only if the key is encrypted."
				{...form.register("passphrase", { onChange: onKeyChanged })}
			/>

			<Controller
				control={form.control}
				name="saveKey"
				render={({ field }) => (
					<Field orientation="horizontal" className="rounded-md border p-3">
						<Checkbox
							id="deploy-save-key"
							checked={field.value}
							onCheckedChange={(v) => field.onChange(v === true)}
						/>
						<FieldContent>
							<FieldLabel htmlFor="deploy-save-key">
								Add this key to the library
							</FieldLabel>
							<FieldDescription>
								Stores it encrypted so the next deploy can pick it by name, and
								so this worker can be redeployed or uninstalled in a click.
								Leave it off and the key is used for this deploy and then
								forgotten — which also means removing this worker later will
								leave its containers running on that machine.
							</FieldDescription>
						</FieldContent>
					</Field>
				)}
			/>

			{saveKey ? (
				<TextField
					id="deploy-key-name"
					label="Name this key"
					placeholder="hetzner root"
					autoComplete="off"
					className="max-w-sm"
					error={errors.keyName}
					description="How it will be listed in the picker and in Settings."
					{...form.register("keyName")}
				/>
			) : null}
		</div>
	);
}

/**
 * The three choices with consequences beyond this deploy.
 *
 * Folded away by default, because every one of them is correct as it stands for a
 * normal VPS — except when the probe has just said otherwise. A machine with no
 * Docker makes `installDocker` the next thing to decide rather than a setting
 * three clicks away, so evidence opens the disclosure and says why.
 */
function Options({
	form,
	facts,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	facts: ProvisionRemoteCheckResponse["facts"];
}) {
	const [open, setOpen] = useState(false);
	const dockerMissing = facts !== null && !facts.hasDocker;

	// Progressive disclosure should react to evidence: a collapsed panel is the
	// right default until the probe has found something inside it that has to be
	// answered. Reopening is left to the operator — this fires on the transition
	// into "Docker is missing", not on every render while it is.
	useEffect(() => {
		if (dockerMissing) setOpen(true);
	}, [dockerMissing]);

	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger asChild>
				<Button type="button" variant="ghost" size="sm" className="w-fit">
					<ChevronDown
						className={`size-3 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
						aria-hidden="true"
					/>
					Advanced
					{dockerMissing && !open ? (
						<Badge variant="warning">1 needs a decision</Badge>
					) : null}
				</Button>
			</CollapsibleTrigger>
			<CollapsibleContent className="grid gap-2 pt-2">
				<ToggleRow
					form={form}
					name="installDocker"
					id="deploy-install-docker"
					title="Install Docker if it is missing"
					description={
						<>
							Downloads <code>get.docker.com</code> and runs it as root on that
							machine. Standard practice, and still your call to make — off by
							default. Without it, a machine with no Docker is refused rather
							than changed.
						</>
					}
					note={
						dockerMissing
							? "That machine has no Docker. Turn this on, or install Docker there yourself — the deploy is refused either way until one of those is true."
							: undefined
					}
				/>
				<ToggleRow
					form={form}
					name="restrictFirewall"
					id="deploy-restrict-firewall"
					title="Restrict the worker port to this manager"
					description="Adds a rule allowing only this manager's address to reach the worker port, and denies it from everywhere else. Applied after the worker is healthy, and not persisted across a reboot on a host with no rule persistence."
					// A security option that quietly does nothing is worse than one that
					// is off, and this one can only work if the far sshd tells us the
					// address to allow. Saying so up front beats a WARN line in a log
					// nobody opens on a deploy that succeeded.
					note="The address to allow comes from the host's own SSH_CONNECTION. On a host whose sshd does not set it, this step is skipped and the deploy says so rather than applying a deny with no matching allow — which would lock this manager out of the worker it just deployed."
				/>
				<ToggleRow
					form={form}
					name="autoUpdate"
					id="deploy-auto-update"
					title="Enable one-click updates"
					description="Deploys a small updater-agent beside the worker so you can roll it to a new image from Updates. Without it, updating this worker means running docker compose pull on that machine yourself."
				/>
			</CollapsibleContent>
		</Collapsible>
	);
}

function ToggleRow({
	form,
	name,
	id,
	title,
	description,
	note,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	name: "installDocker" | "restrictFirewall" | "autoUpdate";
	id: string;
	title: string;
	description: ReactNode;
	/** A condition on this toggle, from the probe or from how it is applied. */
	note?: ReactNode;
}) {
	return (
		<Controller
			control={form.control}
			name={name}
			render={({ field }) => (
				<Field orientation="horizontal" className="rounded-md border p-3">
					<Switch
						id={id}
						checked={field.value}
						onCheckedChange={field.onChange}
					/>
					<FieldContent>
						<FieldLabel htmlFor={id}>{title}</FieldLabel>
						<FieldDescription>{description}</FieldDescription>
						{note ? (
							<FieldDescription className="flex gap-1.5 text-foreground">
								<CircleAlert
									className="mt-0.5 size-3 shrink-0"
									aria-hidden="true"
								/>
								<span>{note}</span>
							</FieldDescription>
						) : null}
					</FieldContent>
				</Field>
			)}
		/>
	);
}

/** One row of the probe's verdict. */
interface CheckRow {
	label: string;
	/** True passed, false blocked, null could not tell. */
	state: boolean | null;
	/** What the probe found. */
	detail: string;
}

/**
 * What the probe found, as a checklist.
 *
 * Was a `dl` of raw facts, which reported `Docker: not installed` and left the
 * operator to work out that this meant the deploy would be refused. Each row now
 * carries an icon and a sentence, so the verdict survives being read in
 * greyscale — the accessibility commitment in PRODUCT.md — and a failing row
 * states the consequence rather than only the fact.
 */
function CheckReport({ report }: { report: ProvisionRemoteCheckResponse }) {
	if (!report.connected) {
		return (
			<CheckNote tone="error" icon={CircleX} title="Could not connect">
				{/* The server's own reason first, because "connection timed out" and
				    "permission denied" send the operator to different fields. Then the
				    consequence, which the reason alone does not give them. */}
				{report.message ? <p>{report.message}</p> : null}
				<p>
					Nothing can be deployed until this succeeds. Check the address and
					port, and that this key is in that user's <code>authorized_keys</code>{" "}
					on the far side.
				</p>
				{report.hostKey ? (
					<FingerprintLine fingerprint={report.hostKey.fingerprint} />
				) : null}
			</CheckNote>
		);
	}

	const facts = report.facts;
	const rows: CheckRow[] = [
		{
			label: "Reachable over SSH",
			state: true,
			detail: facts?.os
				? `Answered as ${facts.os}${facts.arch ? ` on ${facts.arch}` : ""}.`
				: "The machine answered and gave us a shell.",
		},
	];

	if (facts) {
		rows.push(
			{
				label: "Can manage Docker",
				state: facts.isRoot || facts.hasSudo,
				detail: facts.isRoot
					? "Logged in as root."
					: facts.hasSudo
						? "Not root, but has passwordless sudo."
						: "This user is not root and has no passwordless sudo, so it cannot start a container. Deploy as root, or grant sudo without a password.",
			},
			{
				label: "Docker installed",
				state: facts.hasDocker ? facts.dockerUsable : false,
				detail: !facts.hasDocker
					? "No Docker on that machine. The deploy is refused unless you turn on 'Install Docker if it is missing' below."
					: facts.dockerUsable
						? `Docker ${facts.dockerVersion ?? "is installed"} and usable by this user.`
						: `Docker ${facts.dockerVersion ?? "is installed"} but this user cannot talk to its socket, so the deploy would fail at the first command. Add the user to the docker group, or deploy as root.`,
			},
			{
				label: "Compose plugin present",
				state: facts.hasDocker ? facts.hasCompose : null,
				detail: !facts.hasDocker
					? "Cannot tell until Docker is there. Installing Docker brings the plugin with it."
					: facts.hasCompose
						? "docker compose is available."
						: "Docker is there but the compose plugin is not, and the worker is started from a compose file. Install docker-compose-plugin on that machine.",
			},
			{
				label: "Worker port free",
				state: facts.workerPortBound === null ? null : !facts.workerPortBound,
				detail:
					facts.workerPortBound === true
						? "Something is already listening on the worker port. This deploy would start a container that cannot bind it, and the worker would never answer."
						: facts.workerPortBound === false
							? "Nothing is listening on the worker port."
							: "That machine shipped no ss or netstat, so this could not be checked. A port clash would surface as a container that starts and never becomes healthy.",
			},
			{
				label: "No existing worker directory",
				state: facts.existingWorkerDir ? null : true,
				detail: facts.existingWorkerDir
					? "A directory for this worker name is already there. The deploy converges it rather than failing, which is what you want for a retry and not what you want for a name you thought was free."
					: "Nothing of this manager's is on that machine yet.",
			},
		);
	}

	const blocked = rows.some((r) => r.state === false);
	const unknowns = rows.some((r) => r.state === null);

	return (
		<CheckNote
			tone={blocked ? "error" : unknowns ? "warning" : "success"}
			icon={blocked ? CircleX : unknowns ? TriangleAlert : CircleCheck}
			title={
				blocked
					? "Connected, but this machine is not ready"
					: unknowns
						? "Connected, with things worth reading"
						: "Connected and ready"
			}
		>
			<ul className="mt-2 grid gap-1.5">
				{rows.map((row) => (
					<CheckRowItem key={row.label} row={row} />
				))}
			</ul>
			{report.problems.length > 0 ? (
				<div className="mt-3">
					<p className="font-medium">Blockers reported by the server</p>
					<ul className="mt-1 ml-4 list-disc space-y-1">
						{report.problems.map((problem) => (
							<li key={problem}>{problem}</li>
						))}
					</ul>
				</div>
			) : null}
			{report.hostKey ? (
				<FingerprintLine fingerprint={report.hostKey.fingerprint} />
			) : null}
		</CheckNote>
	);
}

const ROW_ICON = {
	pass: CircleCheck,
	fail: CircleX,
	unknown: CircleAlert,
} as const;

const ROW_WORD = {
	pass: "Passed",
	fail: "Blocked",
	unknown: "Worth reading",
} as const;

/**
 * A row of the checklist.
 *
 * The icon and the word carry the state; the colour only reinforces them. A
 * screen reader gets "Blocked: Docker installed" rather than a red circle, and so
 * does anyone reading this on a projector.
 */
function CheckRowItem({ row }: { row: CheckRow }) {
	const kind =
		row.state === true ? "pass" : row.state === false ? "fail" : "unknown";
	const Icon = ROW_ICON[kind];
	return (
		<li className="flex gap-2">
			<Icon
				className={`mt-0.5 size-3.5 shrink-0 ${
					kind === "pass"
						? "text-success"
						: kind === "fail"
							? "text-destructive"
							: "text-warning"
				}`}
				aria-hidden="true"
			/>
			<span className="min-w-0 flex-1">
				<span className="sr-only">{ROW_WORD[kind]}: </span>
				<span className="font-medium">{row.label}</span>
				<span className="text-muted-foreground"> — {row.detail}</span>
			</span>
		</li>
	);
}

/**
 * The key this address is now pinned to.
 *
 * Shown because the deploy trusts whatever answered on first connect. Refusing
 * an unknown host outright would make a one-click deploy impossible; showing the
 * fingerprint is what makes that trade visible instead of silent.
 */
function FingerprintLine({ fingerprint }: { fingerprint: string }) {
	return (
		<p className="mt-3 flex flex-wrap items-center gap-1.5">
			<Badge variant="outline">Host key</Badge>
			<code className="min-w-0 break-all font-mono">{fingerprint}</code>
		</p>
	);
}

const TONE_CLASS = {
	success: "border-success/40",
	warning: "border-warning/40",
	error: "border-destructive/40",
} as const;

const TONE_ICON_CLASS = {
	success: "text-success",
	warning: "text-warning",
	error: "text-destructive",
} as const;

function CheckNote({
	tone,
	icon: Icon,
	title,
	children,
}: {
	tone: "success" | "warning" | "error";
	icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
	title: string;
	children: ReactNode;
}) {
	return (
		// An <output>, so the result of pressing Test connection is announced
		// rather than only coloured — the answer is the entire point of the probe.
		<output
			className={`flex gap-2 rounded-md border p-3 text-xs ${TONE_CLASS[tone]}`}
		>
			<Icon
				className={`mt-0.5 size-4 shrink-0 ${TONE_ICON_CLASS[tone]}`}
				aria-hidden={true}
			/>
			<div className="min-w-0 flex-1">
				<p className="font-medium text-foreground">{title}</p>
				<div className="text-foreground">{children}</div>
			</div>
		</output>
	);
}
